/**
 * The screens nobody designs until the app breaks in front of a child.
 *
 * Every one of these was missing from the wireframes. A blank screen looks
 * broken; a stack trace looks frightening. Each of these says one plain
 * sentence and offers one thing to do.
 */
import type { ReactNode } from 'react'
import { ApiError } from '../api'
import { theme } from '../theme'
import { Button, Card, Page } from './ui'
import { Mascot } from './Mascot'
import { HOURS_ZONE, PRACTICE_HOURS } from '../lib/hours'
import { english, t } from '../lib/i18n'

// `grownUp`: shown on a grown-up's screen, so in the family's language. The
// children's screens leave it off and stay English (lib/i18n.ts).
export function Loading({ what, grownUp = false }: { what?: string; grownUp?: boolean }) {
  const tr = grownUp ? t : english
  return (
    <Page>
      <div style={{ padding: '70px 0', textAlign: 'center', color: theme.faint, fontSize: 16 }}>
        <div style={{ display: 'grid', placeItems: 'center', marginBottom: 10, opacity: 0.9 }}>
          <Mascot size={64} mood="happy" label="" />
        </div>
        {what ?? tr('Loading')}…
      </div>
    </Page>
  )
}

export function ErrorScreen({
  error, onRetry, onHome, grownUp = false,
}: { error: unknown; onRetry?: () => void; onHome?: () => void; grownUp?: boolean }) {
  const tr = grownUp ? t : english
  const api = error instanceof ApiError ? error : null
  const offline = api?.kind === 'offline'

  // Outside practice hours the database sleeps on purpose. Say so, and when
  // to come back, instead of "something went wrong".
  if (api?.kind === 'asleep') {
    return (
      <Page width={460}>
        <Card style={{ textAlign: 'center', marginTop: 60 }}>
          <div style={{ display: 'grid', placeItems: 'center', marginBottom: 8 }}>
            <Mascot size={84} mood="sleepy" label="" />
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, color: theme.text }}>{tr('Word Sprout is asleep')}</h2>
          <p style={{ margin: '0 0 14px', color: theme.muted, fontSize: 16, lineHeight: 1.5 }}>
            {tr('Come back during practice time ({zone}):', { zone: tr(HOURS_ZONE) })}
          </p>
          <div style={{ display: 'inline-block', textAlign: 'left', marginBottom: 18 }}>
            {PRACTICE_HOURS.map((h) => (
              <div key={h.days} style={{ fontSize: 16, color: theme.text2, lineHeight: 1.7 }}>
                <b>{tr(h.days)}</b>: {tr(h.time)}
              </div>
            ))}
          </div>
          <div>{onRetry ? <Button onClick={onRetry}>{tr('Try again')}</Button> : null}</div>
        </Card>
      </Page>
    )
  }

  // What the child reads. Never a code, never a stack.
  const headline = offline ? tr('No internet') : tr('Something went wrong')
  const line = offline
    ? tr('Word Sprout needs the internet to work. Check the connection and try again.')
    : tr('That did not work. Try again in a moment.')

  return (
    <Page width={460}>
      <Card style={{ textAlign: 'center', marginTop: 60 }}>
        <div style={{ display: 'grid', placeItems: 'center', marginBottom: 8 }}>
          <Mascot size={72} mood={offline ? 'sleepy' : 'wonder'} label="" />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 22, color: theme.text }}>{headline}</h2>
        <p style={{ margin: '0 0 22px', color: theme.muted, fontSize: 16, lineHeight: 1.5 }}>
          {line}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          {onRetry ? <Button onClick={onRetry}>{tr('Try again')}</Button> : null}
          {onHome ? <Button tone="quiet" onClick={onHome}>{tr('Go home')}</Button> : null}
        </div>

        {/* The detail the parent needs, tucked away from the child. */}
        {api && !offline ? (
          <details style={{ marginTop: 20, textAlign: 'left' }}>
            <summary style={{ cursor: 'pointer', color: theme.faint, fontSize: 13 }}>
              {tr('For a grown-up')}

            </summary>
            <pre style={{
              marginTop: 8, padding: 10, background: theme.bg, borderRadius: 8,
              fontSize: 12, color: theme.muted, whiteSpace: 'pre-wrap', overflowX: 'auto',
            }}>
              {api.status} · {api.detail}
            </pre>
          </details>
        ) : null}
      </Card>
    </Page>
  )
}

/** A screen with nothing on it yet. Say something rather than showing a void. */
export function Empty({
  emoji, title, line, action,
}: { emoji: string; title: string; line?: string; action?: ReactNode }) {
  return (
    <Card style={{ textAlign: 'center', padding: '36px 24px' }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>{emoji}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: theme.text, marginBottom: 6 }}>
        {title}
      </div>
      {line ? (
        <p style={{ margin: '0 0 18px', color: theme.muted, fontSize: 15, lineHeight: 1.5 }}>
          {line}
        </p>
      ) : null}
      {action}
    </Card>
  )
}

/**
 * Along the top of a session: no connection, or answers still to send.
 *
 * Since 9 Oct 2026 this is true: answers are marked on the device and kept
 * in an outbox until the server has them (lib/outbox.ts). Before that, the
 * bar promised they were saved when nothing saved them. `durable` is false
 * when the browser refused to store them (Codex round 19, finding 4): then
 * they live only in this page, and closing it loses them.
 */
export function OfflineBar({ online, pending = 0, durable = true }: {
  online: boolean
  pending?: number
  durable?: boolean
}) {
  if (online && pending === 0) return null
  const words = pending === 1 ? '1 answer' : `${pending} answers`
  return (
    <div role="status" style={{
      position: 'sticky',
      top: 'env(safe-area-inset-top, 0px)',
      zIndex: 20,
      background: online ? theme.greenPale : theme.orangeDark,
      color: online ? theme.greenDarker : '#FFFFFF',
      fontSize: 14,
      fontWeight: 600,
      padding: '8px 14px',
      textAlign: 'center',
    }}>
      {online
        ? `Saving ${words}…`
        : durable
          ? `No internet. Keep going: your answers are kept on this device${pending ? ` (${words})` : ''} and sent when it comes back.`
          : `No internet. Keep going, and keep this page open: your answers${pending ? ` (${words})` : ''} are sent when it comes back. If it is closed, they will be lost.`}
    </div>
  )
}
